import { toast, ToastOptions } from "react-toastify";
import { ReactNode } from "react";

const defaultOptions: ToastOptions = {
  position: "bottom-center",
  autoClose: 2000,
  hideProgressBar: false,
  closeOnClick: true,
  pauseOnHover: true,
  draggable: true,
  pauseOnFocusLoss: true,
  theme: "light",
};

export const toastSuccess = (message: string | ReactNode, options?: ToastOptions) =>
  toast.success(message, { ...defaultOptions, ...options });

export const toastError = (message: string | ReactNode, options?: ToastOptions) =>
  toast.error(message, { ...defaultOptions, ...options });

export const toastInfo = (message: string, options?: ToastOptions) =>
  toast.info(message, { ...defaultOptions, ...options });

export const toastWarn = (message: string, options?: ToastOptions) =>
  toast.warn(message, { ...defaultOptions, ...options });

export const toastLoading = (message: string, options?: ToastOptions) =>
  toast.loading(message, { ...defaultOptions, ...options });

export const toastDismiss = (id?: React.ReactText) => toast.dismiss(id);

const defaultPromiseMessages = {
  pending: "Processing...",
  success: "Done!",
  error: "Error"
};

export const toastPromise = async <T,>(
  promise: Promise<T>,
  messages?: Partial<{ pending: string; success: string; error: string | ((error: any) => string) }>,
  options?: ToastOptions
) => {
  const getErrorMessage = (error: any): string => {
    if (messages?.error && typeof messages.error === 'function') {
      return messages.error(error);
    }
    if (messages?.error && typeof messages.error === 'string') {
      return messages.error;
    }
    if (error?.response?.data) {
      const errorData = error.response.data;
      if (typeof errorData === 'string') {
        return errorData;
      }
      if (errorData?.message) {
        return errorData.message;
      }
      if (errorData?.error) {
        return errorData.error;
      }
      if (typeof errorData === 'object') {
        return JSON.stringify(errorData);
      }
    }
    if (error?.message) {
      return error.message;
    }
    return defaultPromiseMessages.error;
  };

  const toastId = toast.loading(messages?.pending ?? defaultPromiseMessages.pending, { ...defaultOptions, ...options });

  try {
    const result = await promise;
    toast.update(toastId, {
      render: messages?.success ?? defaultPromiseMessages.success,
      type: "success",
      isLoading: false,
      ...defaultOptions,
      ...options,
    });
    return result;
  } catch (error: any) {
    const errorMessage = getErrorMessage(error);
    toast.update(toastId, {
      render: errorMessage,
      type: "error",
      isLoading: false,
      ...defaultOptions,
      ...options,
    });
    throw error;
  }
};